import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ScrollBox } from '~/components/blocks';
import { SearchAllPatientsDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { patientPageInfoState } from '~/state/patient/atoms';
import { formatISOString } from '~/utils/date';

export const useFetchAllPatients = () => {
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);

  const [appointmentEvent, clearAppointmentEvent] = useSubscriptions('Appointment', [
    'created',
    'updated',
  ]);
  const [checkinEntryEvent, clearCheckinEntryEvent] = useSubscriptions('CheckinEntry', ['created']);
  const [outpatientQuestionnaireEntryEvent, clearOutpatientQuestionnaireEntryEvent] =
    useSubscriptions('OutpatientQuestionnaireEntry', ['created']);
  const [medicationFollowupEvent, clearMedicationFollowupEvent] = useSubscriptions(
    'MedicationFollowupQuestionnaireSheet',
    ['created', 'updated'],
  );

  const [state, setPageState] = useAtom(patientPageInfoState);
  const [searchAllPatients, { called, loading, data }] = useLazyQuery(SearchAllPatientsDocument, {
    fetchPolicy: 'network-only',
  });

  const search = useCallback(async () => {
    await searchAllPatients({
      variables: {
        searchWord: state.searchWord,
        latestMessageCreatedAt: formatISOString(state.latestMessageCreatedAt),
        isFollowingUp: state.isFollowingUp,
        answered: state.answered,
        practitionerId: state.practitionerId,
        page: state.page,
        perPage: state.perPage,
        orderBy: state.orderBy,
      },
    });
  }, [
    searchAllPatients,
    state.searchWord,
    state.latestMessageCreatedAt,
    state.isFollowingUp,
    state.answered,
    state.practitionerId,
    state.page,
    state.perPage,
    state.orderBy,
  ]);

  // 連続して検索条件が変化した時に都度リクエストしないようにする
  const searchDebounce = useDebouncedCallback(search, 300);
  const allPatients = data ? getMe(data)?.organization.allPatients.nodes || [] : [];

  useEffect(() => {
    if (!data) return;
    const allPatientsMeta = getMe(data)?.organization.allPatients;

    setPageState((_state) => ({
      ..._state,
      totalCount: allPatientsMeta?.nodesCount || 0,
      totalPage: allPatientsMeta?.pagesCount || 1,
    }));
  }, [data, setPageState]);

  useEffect(() => {
    searchDebounce();
    clearCheckinEntryEvent();
    clearOutpatientQuestionnaireEntryEvent();
    clearAppointmentEvent();
    clearMedicationFollowupEvent();
  }, [
    searchDebounce,
    clearCheckinEntryEvent,
    clearOutpatientQuestionnaireEntryEvent,
    clearAppointmentEvent,
    clearMedicationFollowupEvent,
    state.searchWord,
    state.latestMessageCreatedAt,
    state.isFollowingUp,
    state.answered,
    state.practitionerId,
    state.page,
    state.perPage,
    state.orderBy,
  ]);

  useEffect(() => {
    if (!checkinEntryEvent) {
      return;
    }

    if (['created'].includes(checkinEntryEvent.actionName)) {
      search();
    }
  }, [search, checkinEntryEvent]);

  useEffect(() => {
    if (!outpatientQuestionnaireEntryEvent) {
      return;
    }

    if (['created'].includes(outpatientQuestionnaireEntryEvent.actionName)) {
      search();
    }
  }, [search, outpatientQuestionnaireEntryEvent]);

  useEffect(() => {
    if (!appointmentEvent) {
      return;
    }

    if (['created', 'updated'].includes(appointmentEvent.actionName)) {
      search();
    }
  }, [search, appointmentEvent]);

  useEffect(() => {
    if (!medicationFollowupEvent) {
      return;
    }

    if (['created', 'updated'].includes(medicationFollowupEvent.actionName)) {
      search();
    }
  }, [search, medicationFollowupEvent]);

  return { loading: !called || loading, allPatients, scrollRef };
};
